import React from "react";
import { Link } from "react-router-dom";
import { blogPosts } from "../data/blogData";
import "../styles/Diary.css";
import { Helmet } from "react-helmet";
import AdSenseBlock from "../components/AdSenseBlock";
import { AD_SLOTS } from "../constants/adSlots";

// ▼ カテゴリごとにグループ化
const groupedByCategory = blogPosts.reduce((acc, post) => {
  const cat = post.category || "未分類";
  if (!acc[cat]) acc[cat] = [];
  acc[cat].push(post);
  return acc;
}, {});

// ▼ 各カテゴリ内を日付の降順でソート（★追加）
Object.keys(groupedByCategory).forEach(cat => {
  groupedByCategory[cat].sort(
    (a, b) => new Date(b.date) - new Date(a.date)
  );
});

// ▼ 表示順
const categoryOrder = [
  "レース体験",
  "装備や準備",
  "トレーニング記録",
  "はじめての記録",
  "トレランの学び",
  "その他・トレランの記録",
  "Trail Running Notes (EN)",
  "未分類"
];

// ▼ 表示ラベル
const categoryLabels = {
  "Trail Running Notes (EN)": "Trail Running Notes (English)",
  "レース体験": "レース体験",
  "装備や準備": "装備や準備",
  "トレーニング記録": "トレーニング記録",
  "はじめての記録": "はじめての記録",
  "トレランの学び": "トレランの学び",
  "その他・トレランの記録": "その他・トレランの記録",
  "未分類": "未分類"
};

export default function BlogList() {
  const categoryList = categoryOrder.filter(cat => groupedByCategory[cat]);

  const formatDate = (raw) =>
    new Date(raw).toLocaleDateString("ja-JP", {
      year: "numeric",
      month: "long",
      day: "numeric",
    });

  return (
    <div className="diary-container" style={{ maxWidth: "820px", margin: "0 auto" }}>
      <Helmet>
        <title>トレラン開発者の体験ブログ｜Trail Run Simulator トレランの記録</title>
        <meta
          name="description"
          content="トレラン初心者・中級者に役立つ体験談を開発者視点で紹介。GPX活用、UI改善の裏話、装備レビュー、初レース挑戦記など、リアルな山の記録をブログ形式で綴ります。"
        />
        <meta
          name="keywords"
          content="トレラン, トレイルランニング, 開発者ブログ, GPX活用, UI改善, 装備, 初心者体験談"
        />
        <script type="application/ld+json">
          {JSON.stringify({
            "@context": "https://schema.org",
            "@type": "Blog",
            "name": "Trail Run Simulator トレランの記録",
            "url": "https://trailrunsim.com/blog",
            "description":
              "Trail Run Simulatorの開発者によるトレラン体験談や改善へのこだわりを記録したブログ。",
            "author": {
              "@type": "Person",
              "name": "Trail Run Simulator 開発者",
            },
          })}
        </script>
      </Helmet>

      {/* 🍏 タイトル */}
    <h1
  style={{
    marginBottom: "1rem",
    fontSize: "28px",
    fontWeight: 600,
    color: "#1c1c1e",
    lineHeight: 1.3
  }}
>
  中の人のトレラン記録
</h1>


      <p
        className="diary-intro"
        style={{
          marginBottom: "1.6rem",
          color: "#444",
          lineHeight: 1.7
        }}
      >
        TrailRunSim 開発者が、トレランを始めたきっかけ、初めて山を走った日のこと、
        初レースの体験談、日々のトレーニングや気づきなどを気ままに綴るブログです。
      </p>

      <AdSenseBlock adSlot={AD_SLOTS.BLOG} />

      {/* 🍏 カテゴリ目次 */}
      <ul
        className="category-nav"
        style={{
          display: "flex",
          flexWrap: "wrap",
          gap: "0.6rem",
          margin: "1.6rem 0"
        }}
      >
        {categoryList.map((category) => (
          <li key={category} style={{ listStyle: "none" }}>
            <a
              href={`#category-${category}`}
              style={{
                padding: "0.4rem 0.8rem",
                background: "#f2f2f7",
                borderRadius: "8px",
                textDecoration: "none",
                color: "#007aff",
                fontWeight: 500
              }}
            >
              {categoryLabels[category] || category}
            </a>
          </li>
        ))}
      </ul>

      {/* 🍏 カテゴリごとの記事一覧 */}
      {categoryList.map((category) => (
        <section
          key={category}
          id={`category-${category}`}
          className="diary-category-block"
          style={{ marginTop: "2.4rem" }}
        >
          <h2
            className="diary-category-title"
            style={{
              marginBottom: "1rem",
              paddingBottom: "0.4rem",
              borderBottom: "1px solid #e5e5ea",
              color: "#222"
            }}
          >
            {categoryLabels[category] || category}
          </h2>

          {groupedByCategory[category].map((post) => (
            <div
              key={post.id}
              className="diary-list-item"
              style={{
                marginBottom: "1.6rem",
                paddingBottom: "1rem",
                borderBottom: "1px solid #f2f2f7"
              }}
            >
              <h3 style={{ marginBottom: "0.2rem" }}>
                <Link
                  to={`/blog/${post.id}`}
                  style={{ color: "#007aff", textDecoration: "none", fontWeight: 600 }}
                >
                  {post.title}
                </Link>
              </h3>

              <p className="diary-date" style={{ color: "#666", marginBottom: "0.4rem" }}>
                {formatDate(post.date)}
              </p>

              <p style={{ color: "#444", lineHeight: 1.6 }}>{post.summary}</p>
            </div>
          ))}
        </section>
      ))}

      {/* 🍏 関連ナビ */}
      <div
        className="diary-related"
        style={{
          marginTop: "3rem",
          padding: "1rem",
          background: "#f8f8f8",
          borderRadius: "12px",
          lineHeight: 1.6
        }}
      >
        <p>
          トレランの基礎知識は{" "}
          <Link to="/basics" style={{ color: "#007aff" }}>
            こちら
          </Link>
          。
        </p>
        <p>
          シミュレータの使い方は{" "}
          <Link to="/how-to-use" style={{ color: "#007aff" }}>
            使い方ガイド
          </Link>
          をご覧ください。
        </p>
      </div>
    </div>
  );
}
